import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Analytics,
  Comment,
  AttachFile,
  Schedule,
  TrendingUp,
  CalendarToday,
  AccessTime,
  Assignment,
} from '@mui/icons-material';

const LeadMetrics = ({ lead }) => {
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getActivityScore = () => {
    // Calculate activity score based on comments, documents, and recency
    const comments = lead.total_comments || 0;
    const documents = lead.total_documents || 0;
    const daysSince = lead.days_since_created || 0;

    let score = (comments * 10) + (documents * 15);
    if (daysSince < 7) score += 20;
    else if (daysSince < 30) score += 10;

    return Math.min(100, score);
  };

  const getUrgencyLevel = () => {
    const daysSince = lead.days_since_created || 0;
    const priority = lead.priority;

    if (priority === 'high' && daysSince > 3) return { level: 'High', color: 'error' };
    if (priority === 'high' && daysSince <= 3) return { level: 'Critical', color: 'error' };
    if (priority === 'medium' && daysSince > 7) return { level: 'Medium', color: 'warning' };
    if (priority === 'low' || daysSince <= 1) return { level: 'Low', color: 'success' };
    return { level: 'Normal', color: 'info' };
  };

  const activityScore = getActivityScore();
  const urgency = getUrgencyLevel();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Analytics color="primary" />
          <Typography variant="h6">Lead Metrics</Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Activity Metrics */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp color="primary" />
                  <Typography variant="subtitle2">Activity Level</Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Activity Score: {activityScore}/100
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={activityScore}
                    color={activityScore > 60 ? 'success' : activityScore > 30 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Comment fontSize="small" color="action" />
                      <Typography variant="caption">
                        {lead.total_comments || 0} comments
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFile fontSize="small" color="action" />
                      <Typography variant="caption">
                        {lead.total_documents || 0} documents
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Timing Metrics */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule color="primary" />
                  <Typography variant="subtitle2">Timing</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2">
                      Created: {lead.days_since_created || 0} days ago
                    </Typography>
                  </Box>

                  {lead.last_contacted && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2">
                        Last contact: {formatTimeAgo(lead.last_contacted)}
                      </Typography>
                    </Box>
                  )}

                  {lead.next_follow_up && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule fontSize="small" color="warning" />
                      <Typography variant="body2" color="warning.main">
                        Next follow-up: {lead.next_follow_up}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Urgency & Priority */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Assignment color="primary" />
                  <Typography variant="subtitle2">Priority & Urgency</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Priority:</Typography>
                    <Chip
                      label={lead.priority?.toUpperCase() || 'Not Set'}
                      color={
                        lead.priority === 'high' ? 'error' :
                        lead.priority === 'medium' ? 'warning' : 'success'
                      }
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Urgency:</Typography>
                    <Chip
                      label={urgency.level}
                      color={urgency.color}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Assignment Info */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 24, height: 24 }}>
                    <Assignment fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2">Assignment</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {lead.assigned_to_full_name && (
                    <Typography variant="body2">
                      <strong>Assigned to:</strong> {lead.assigned_to_full_name}
                    </Typography>
                  )}

                  {lead.assigned_to_role && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Role:</Typography>
                      <Chip
                        label={lead.assigned_to_role.toUpperCase()}
                        color={
                          lead.assigned_to_role === 'admin' ? 'error' :
                          lead.assigned_to_role === 'manager' ? 'warning' :
                          lead.assigned_to_role === 'team_lead' ? 'info' : 'success'
                        }
                        size="small"
                      />
                    </Box>
                  )}

                  {lead.assigned_at && (
                    <Typography variant="caption" color="text.secondary">
                      Assigned: {formatTimeAgo(lead.assigned_at)}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LeadMetrics;